<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <section class="page-hero no-print">
        <div class="container">
          <nav class="breadcrumb-nav">
            <a href="/" class="breadcrumb-link">Home</a>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Verify Construction Permit</span>
          </nav>
          <h1 class="page-title">Verify Construction Permit</h1>
          <p class="page-subtitle">Confirm a construction permit by application number or scan the QR on the printed certificate.</p>
        </div>
      </section>

      <!-- Search box: shown until we successfully load a permit. -->
      <section v-if="!valid && !loading" class="content-section no-print">
        <div class="container" style="max-width:720px;">
          <div class="verify-card">
            <label class="verify-label">Application Number</label>
            <div class="verify-row">
              <input type="text" class="verify-input" placeholder="e.g. CP/2026/00042 or 187"
                     v-model="query" @keyup.enter="lookup" :disabled="loading">
              <button class="verify-btn" @click="lookup" :disabled="loading || !query.trim()">
                <span v-if="loading">Verifying…</span>
                <span v-else>Verify</span>
              </button>
            </div>
            <small class="verify-hint">Tip: scan the QR code on a printed certificate to verify it instantly.</small>
            <p v-if="notFoundMsg" class="verify-error">{{ notFoundMsg }}</p>
          </div>
        </div>
      </section>

      <!-- Verdict banner -->
      <section v-if="loaded" class="content-section no-print" style="padding-top:24px;padding-bottom:0;">
        <div class="container" style="max-width:920px;">
          <div class="verdict-banner" :class="valid ? 'ok' : 'bad'">
            <div class="verdict-icon">{{ valid ? '✓' : '!' }}</div>
            <div class="verdict-body">
              <h3>{{ valid ? 'Permit is VALID' : 'Permit NOT FOUND' }}</h3>
              <p v-if="valid">
                Application <strong>{{ application.application_number || '—' }}</strong> ·
                Issued to <strong>{{ application.owner_full_names || '—' }}</strong>
                <span v-if="application.plot_number"> · Plot <strong>{{ application.plot_number }}</strong></span>
              </p>
              <p v-else>
                We couldn't find a permit for <code>{{ lastQueried }}</code>. Check the application number and try again.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Floating Print FAB -->
      <button v-if="valid" @click="printCert" class="print-fab no-print" type="button">
        <span aria-hidden="true">🖨</span>
        <span class="print-fab-label">Print / Save as PDF</span>
      </button>

      <!-- Certificate render (visual + print target) -->
      <section v-if="valid" class="cert-section">
        <div class="cert-outer">
          <div class="page-container"
               :style="{ backgroundImage: bgLink ? ('url(' + bgLink + ')') : 'none' }">

            <img v-if="qrLink" :src="qrLink" alt="qr"
                 style="position:absolute;top:0.55in;left:0.55in;width:1.1in;height:1.1in;">

            <div style="position:absolute;top:0.55in;right:0.55in;text-align:right;">
              <div style="font:700 15pt Aldrich,sans-serif;color:#000;letter-spacing:1px;">
                Serial NO. {{ application.application_number || '—' }}
              </div>
              <div style="margin-top:6px;width:22px;height:22px;border:1.5px solid #000;display:inline-block;"></div>
            </div>

            <div style="position:absolute;top:1.7in;left:0;right:0;text-align:center;">
              <img v-if="county.logo" :src="county.logo" alt="county-coat"
                   style="height:1.55in;max-width:2in;object-fit:contain;">
            </div>

            <div style="position:absolute;top:3.6in;left:0.6in;right:0.6in;text-align:center;">
              <div style="font:800 22pt Montserrat,sans-serif;color:#111;text-transform:uppercase;letter-spacing:0.5px;line-height:1.15;">
                County Government of {{ (county.countyName || '').toUpperCase() }}
              </div>
              <div style="font:700 12pt Montserrat,sans-serif;color:#111;text-transform:uppercase;margin-top:6px;letter-spacing:0.5px;">
                Land, Physical Planning, Housing &amp; Urban Development
              </div>
            </div>

            <!-- Cursive title (nowrap so it never wraps into paragraph). -->
            <div style="position:absolute;top:4.8in;left:0.6in;right:0.6in;text-align:center;">
              <span style="font:400 42pt Norican,cursive;color:#111;white-space:nowrap;display:inline-block;">Construction Permit</span>
            </div>

            <div style="position:absolute;top:5.9in;left:0.7in;right:0.7in;text-align:center;
                        font:400 12pt 'Fira Sans',sans-serif;color:#111;line-height:1.55;">
              I hereby certify that the construction permit application for
              <strong>{{ application.owner_full_names || '—' }}</strong>
              of ID Number <strong>{{ application.owner_id_number || '—' }}</strong>
              has been <strong>approved</strong> to be undertaken at plot Number
              <strong>{{ application.plot_number || '—' }}</strong>
              having met all the requirements.
            </div>

            <div style="position:absolute;top:7.6in;left:0.6in;right:0.6in;font:400 11pt 'Fira Sans',sans-serif;color:#111;">
              <div><strong>Project:</strong> {{ application.project_title || '—' }}</div>
              <div><strong>Development Type:</strong> {{ application.development_type || '—' }}</div>
              <div><strong>Sub County:</strong> {{ application.subCountyName || '—' }}</div>
              <div><strong>Ward:</strong> {{ application.wardName || '—' }}</div>
              <div><strong>Approved On:</strong> {{ formatDate(application.updated_at) }}</div>
            </div>

            <div style="position:absolute;bottom:1.05in;right:1.1in;text-align:center;">
              <img v-if="rubberStampUrl || sealConstruction" :src="rubberStampUrl || sealConstruction" alt="seal"
                   style="height:1.35in;width:auto;">
              <div style="margin-top:4px;background:#c9d5e6;color:#1c4a94;font:700 10pt 'Fira Sans',sans-serif;
                          padding:4px 12px;letter-spacing:2px;border-radius:3px;display:inline-block;">
                APPROVED
              </div>
            </div>

            <img v-if="poweredByLogo" :src="poweredByLogo" alt="powered"
                 style="position:absolute;bottom:1.15in;left:1.1in;height:0.5in;">

            <div style="position:absolute;bottom:0.7in;left:1.1in;right:1.1in;
                        font:400 10pt 'Fira Sans',sans-serif;color:#111;">
              <strong>NB:</strong> This permit does not exempt the holder from obeying existing laws.
            </div>

            <div style="position:absolute;bottom:0.35in;left:1.1in;right:1.1in;
                        display:flex;justify-content:space-between;gap:0.5in;
                        font:400 8pt 'Fira Sans',sans-serif;color:#111;">
              <span>
                <strong>County Government of {{ county.countyName }},</strong>
                | {{ county.address || 'P.O Box —, ' + (county.countyName || '') }}
              </span>
              <span>
                <strong>Contacts, T:</strong> {{ county.contact || '—' }}
                <strong> E:</strong> {{ county.email || '—' }}
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent class="no-print" />
  </div>
</template>

<script>
/* eslint-disable */
// Public Construction-Permit verification / print page. Hit by the QR on
// every printed permit (RMS-MAIN ConstructionPermits.vue encodes
// <selfService>/#/verify-construction/<applicationNumber>). Renders the
// permit with a validity banner + Print / Save-as-PDF button.
import { execute, econstruction, biller, base } from '@/api';
import HeaderComponent from '@/components/HeaderComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';

export default {
  name: 'VerifyConstruction',
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      query: '',
      lastQueried: '',
      loading: false,
      loaded: false,
      valid: false,
      notFoundMsg: '',

      sealConstruction: base + '/logo/seal-construction.png',
      poweredByLogo:    base + '/logo/poweredbylogo.png',
      rubberStampUrl:   '',
      // Background image URL is computed at runtime via `base` so webpack
      // doesn't try to resolve it as a module import during build.
      bgLink:           base + '/logo/bg-image-construction.jpg',
      qrLink: '',

      application: {},
      county: {},
    };
  },
  mounted() {
    this.loadRubberStamp();
    const fromParam = this.$route && this.$route.params && this.$route.params.applicationId;
    if (fromParam) {
      this.query = decodeURIComponent(fromParam);
      this.lookup();
    }
  },
  methods: {
    printCert() { window.print(); },
    formatDate(s) {
      if (!s) return '—';
      const d = new Date(String(s).replace(' ', 'T'));
      if (isNaN(d.getTime())) return s;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${('0'+d.getDate()).slice(-2)} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    loadRubberStamp() {
      try {
        const fd = new FormData();
        fd.append('function', 'getModuleSettings');
        fd.append('module', 'plans');
        execute(fd, biller).then(res => {
          if (res && res.data && res.data.success && res.data.data && res.data.data.rubberStampUrl) {
            this.rubberStampUrl = res.data.data.rubberStampUrl + '?v=' + Date.now();
          }
        }).catch(() => {});
      } catch (e) { /* ignore */ }
    },
    lookup() {
      const id = String(this.query || '').trim();
      if (!id) return;
      this.notFoundMsg = '';
      this.loading = true;
      const fd = new FormData();
      fd.append('function', 'printConstructionPermit');
      fd.append('applicationId', id);
      execute(fd, econstruction).then(res => {
        this.loading = false;
        this.loaded = true;
        this.lastQueried = id;
        if (res && res.data && res.data.success && res.data.data && res.data.data.application) {
          this.valid = true;
          this.application = res.data.data.application || {};
          this.county      = res.data.data.county || {};
          if (this.county.poweredByLogo) this.poweredByLogo = this.county.poweredByLogo;
          const selfSvc = this.county.selfService ? String(this.county.selfService).replace(/\/$/, '') : '';
          const key = this.application.application_number || id;
          if (selfSvc && key) {
            this.qrLink = 'https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=' +
                          encodeURIComponent(selfSvc + '/#/verify-construction/' + encodeURIComponent(key));
          }
        } else {
          this.valid = false;
          this.application = {};
          this.notFoundMsg = (res && res.data && res.data.message) || 'Permit not found';
        }
      }).catch(() => {
        this.loading = false;
        this.loaded = true;
        this.valid = false;
        this.notFoundMsg = 'Network error — please retry.';
      });
    },
  },
};
</script>

<style scoped>
.page-wrapper { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; }
.main-content { flex: 1; padding-top: 72px; }
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); padding: 48px 0; color: #fff; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.breadcrumb-nav { display:flex; gap:8px; margin-bottom:16px; font-size:14px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration:none; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color:#fff; }
.page-title { font-size: 30px; font-weight: 800; margin-bottom: 6px; }
.page-subtitle { opacity: 0.9; font-size: 15px; }

.content-section { padding: 40px 0; }

.verify-card { background:#fff; border-radius:16px; padding:24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.verify-label { display:block; font-weight:600; color:#1e293b; margin-bottom:10px; }
.verify-row { display:flex; gap:10px; }
.verify-input { flex:1; padding:12px 14px; border:2px solid #e2e8f0; border-radius:10px; font-size:15px; }
.verify-btn { padding:12px 20px; background:#0f4c81; color:#fff; border:0; border-radius:10px; font-weight:600; cursor:pointer; }
.verify-btn:disabled { opacity:0.6; cursor:not-allowed; }
.verify-hint { display:block; margin-top:10px; color:#64748b; }
.verify-error { color:#dc2626; margin-top:12px; font-weight:500; }

.verdict-banner { display:flex; gap:16px; align-items:center; padding:20px 24px; border-radius:14px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.verdict-banner.ok  { background:#d1fae5; }
.verdict-banner.bad { background:#fee2e2; }
.verdict-icon { width:52px; height:52px; border-radius:50%; background:#fff; display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:700; flex-shrink:0; }
.verdict-banner.ok  .verdict-icon { color:#059669; }
.verdict-banner.bad .verdict-icon { color:#dc2626; }
.verdict-body h3 { margin:0 0 4px 0; font-size:18px; font-weight:700; color:#1e293b; }
.verdict-body p  { margin:0; font-size:14px; color:#334155; }

.print-fab {
  position: fixed; top: 88px; right: 24px; z-index: 1050;
  display: inline-flex; align-items: center; gap: 8px;
  background: #0f4c81; color: #fff; border: 0; border-radius: 999px;
  padding: 12px 20px; font: 600 14px/1.2 var(--font-body, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  cursor: pointer; box-shadow: 0 6px 20px rgba(15,76,129,0.35); white-space: nowrap;
}
.print-fab:hover { background:#0a3459; }
@media (max-width: 480px) { .print-fab { padding: 12px 14px; } .print-fab-label { display: none; } }

.cert-section { padding: 24px 0 60px; background:#eaeaea; }
.cert-outer   { background:#eaeaea; }
.page-container {
  width:8.268in; height:11.693in; position:relative;
  margin:0 auto; padding:0; background:#fff;
  /* Background image is applied via :style binding on the element
     itself (see template) — keeping the URL out of scoped CSS so
     webpack doesn't try to resolve it as a module import. */
  background-size:100% 100%; background-repeat:no-repeat;
}

@media print {
  .no-print { display: none !important; }
  .main-content { padding-top: 0 !important; }
  .cert-section { background: #fff !important; padding: 0 !important; }
  .cert-outer   { background: #fff !important; }
  .page-wrapper { background: #fff !important; }
}
</style>
